import React, { useState, useEffect, useMemo, forwardRef, useImperativeHandle, useRef } from 'react';
import { EditDialog, DeleteDialog, CreateDialog, DurationInput, SessionDetailsModal } from './shared';
import MonthCalendar from './MonthCalendar';
import { toast } from 'react-toastify';
import { authFetch } from '../utils/api';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

// Stable color palette for per-session dots. Hash session._id → index so the
// color is deterministic across re-renders, month navigation, and refresh.
const SESSION_DOT_PALETTE = [
  '#ef4444', '#f97316', '#eab308', '#22c55e',
  '#14b8a6', '#3b82f6', '#8b5cf6', '#ec4899',
];
const colorForSession = (id) => {
  let hash = 0;
  for (let i = 0; i < id.length; i += 1) hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
  return SESSION_DOT_PALETTE[hash % SESSION_DOT_PALETTE.length];
};

const dayKey = (year, monthIdx, day) => `${year}-${monthIdx}-${day}`;

// Sunday of the week containing `d` (local time, midnight).
const startOfWeek = (d) => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    x.setDate(x.getDate() - x.getDay());
    return x;
};

const addDays = (d, n) => {
    const x = new Date(d);
    x.setDate(x.getDate() + n);
    return x;
};

// "April 2026" or "April - May 2026" or "December 2025 - January 2026".
const formatWeekHeader = (weekStart) => {
    const weekEnd = addDays(weekStart, 6);
    const sameMonth =
        weekStart.getMonth() === weekEnd.getMonth() &&
        weekStart.getFullYear() === weekEnd.getFullYear();
    const sameYear = weekStart.getFullYear() === weekEnd.getFullYear();
    if (sameMonth) {
        return weekStart.toLocaleString('default', { month: 'long', year: 'numeric' });
    }
    if (sameYear) {
        const a = weekStart.toLocaleString('default', { month: 'long' });
        const b = weekEnd.toLocaleString('default', { month: 'long' });
        return `${a} - ${b} ${weekStart.getFullYear()}`;
    }
    const a = weekStart.toLocaleString('default', { month: 'long', year: 'numeric' });
    const b = weekEnd.toLocaleString('default', { month: 'long', year: 'numeric' });
    return `${a} - ${b}`;
};

const SessionHistory = forwardRef(({ onClose, onSessionsUpdate, isExiting }, ref) => {
    const [sessionHistory, setSessionHistory] = useState([]);
    const [editingSession, setEditingSession] = useState(null);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [sessionToDelete, setSessionToDelete] = useState(null);
    const [isClearHistoryDialogOpen, setIsClearHistoryDialogOpen] = useState(false);
    const [editingFields, setEditingFields] = useState({
        text: '',
        date: '',
        time: '',
        duration: ''
    });
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [durationValue, setDurationValue] = useState('00:00:00');
    const [durationError, setDurationError] = useState('');
    const [projects, setProjects] = useState([]);
    const [editProjectId, setEditProjectId] = useState('');
    const [createProjectId, setCreateProjectId] = useState('');
    const [createDate, setCreateDate] = useState(() => new Date().toISOString().split('T')[0]);
    const [createTime, setCreateTime] = useState(() => new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' }));
    const fileInputRef = useRef(null);

    // Calendar view state — month currently displayed, plus the day whose
    // details modal is open (null when closed).
    const [currentMonth, setCurrentMonth] = useState(() => new Date());
    const [selectedDay, setSelectedDay] = useState(null);
    // 'month' | 'week'. Week mode tracks its own visible week. Persisted in
    // localStorage so the user's preferred view survives reloads. Default
    // (and fallback for any unexpected stored value) is 'week'.
    const [viewMode, setViewMode] = useState(() => {
        try {
            const stored = localStorage.getItem('sessionHistoryViewMode');
            return stored === 'week' || stored === 'month' ? stored : 'week';
        } catch (err) {
            console.warn('Failed to read sessionHistoryViewMode from localStorage:', err);
            return 'week';
        }
    });
    useEffect(() => {
        try {
            localStorage.setItem('sessionHistoryViewMode', viewMode);
        } catch (err) {
            console.warn('Failed to persist sessionHistoryViewMode to localStorage:', err);
        }
    }, [viewMode]);
    const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
    // Drives the cross-fade between month/week views. Held for half the
    // total transition (~150ms) before the underlying view swaps.
    const [isViewFading, setIsViewFading] = useState(false);
    const today = useMemo(() => new Date(), []);

    // Sessions grouped by local-date day key — recomputed only when the
    // session list changes, not on every render of the calendar.
    const sessionsByDay = useMemo(() => {
        const map = {};
        for (const s of sessionHistory) {
            const d = new Date(s.startedAt);
            const key = dayKey(d.getFullYear(), d.getMonth(), d.getDate());
            if (!map[key]) map[key] = [];
            map[key].push(s);
        }
        return map;
    }, [sessionHistory]);

    // Unique year-month keys that contain at least one session, sorted ascending.
    // Format: "YYYY-MM" with zero-padded month so lexical sort = chronological.
    const sortedMonthKeys = useMemo(() => {
        const set = new Set();
        for (const s of sessionHistory) {
            const d = new Date(s.startedAt);
            set.add(`${d.getFullYear()}-${String(d.getMonth()).padStart(2, '0')}`);
        }
        return Array.from(set).sort();
    }, [sessionHistory]);

    const currentMonthKey = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth()).padStart(2, '0')}`;

    // Find the closest populated month strictly before / after the current key.
    // Works even when `currentMonth` itself is unpopulated — important for the
    // case where the user deletes the last session in the visible month.
    const prevPopulatedKey = useMemo(() => {
        for (let i = sortedMonthKeys.length - 1; i >= 0; i -= 1) {
            if (sortedMonthKeys[i] < currentMonthKey) return sortedMonthKeys[i];
        }
        return null;
    }, [sortedMonthKeys, currentMonthKey]);

    const nextPopulatedKey = useMemo(() => {
        for (const k of sortedMonthKeys) {
            if (k > currentMonthKey) return k;
        }
        return null;
    }, [sortedMonthKeys, currentMonthKey]);

    const canGoPrev = prevPopulatedKey !== null;
    const canGoNext = nextPopulatedKey !== null;

    const goToPrevMonth = () => {
        if (!prevPopulatedKey) return;
        const [y, m] = prevPopulatedKey.split('-').map(Number);
        setCurrentMonth(new Date(y, m, 1));
    };
    const goToNextMonth = () => {
        if (!nextPopulatedKey) return;
        const [y, m] = nextPopulatedKey.split('-').map(Number);
        setCurrentMonth(new Date(y, m, 1));
    };

    // Week-mode navigation: shift weekStart by ±7 days, bounded by earliest /
    // latest session dates (not by populated-week-only — empty weeks within
    // the range are fine to land on at a 7-day granularity).
    const sessionDateBounds = useMemo(() => {
        if (sessionHistory.length === 0) return { earliest: null, latest: null };
        let earliest = null;
        let latest = null;
        for (const s of sessionHistory) {
            const d = new Date(s.startedAt);
            if (!earliest || d < earliest) earliest = d;
            if (!latest || d > latest) latest = d;
        }
        return { earliest, latest };
    }, [sessionHistory]);

    const canGoPrevWeek = sessionDateBounds.earliest !== null && weekStart > sessionDateBounds.earliest;
    const canGoNextWeek =
        sessionDateBounds.latest !== null && addDays(weekStart, 7) <= sessionDateBounds.latest;

    const goToPrevWeek = () => {
        if (!canGoPrevWeek) return;
        setWeekStart((w) => addDays(w, -7));
    };
    const goToNextWeek = () => {
        if (!canGoNextWeek) return;
        setWeekStart((w) => addDays(w, 7));
    };

    // Unified handlers that the JSX calls; they branch on viewMode.
    const handlePrev = viewMode === 'week' ? goToPrevWeek : goToPrevMonth;
    const handleNext = viewMode === 'week' ? goToNextWeek : goToNextMonth;
    const canPrev = viewMode === 'week' ? canGoPrevWeek : canGoPrev;
    const canNext = viewMode === 'week' ? canGoNextWeek : canGoNext;

    const switchView = (next) => {
        if (next === viewMode || isViewFading) return;
        setIsViewFading(true);
        // Hold the fade for ~150ms (half the total animation), swap underlying
        // view state at the midpoint so the user sees the new view fade back in.
        window.setTimeout(() => {
            setViewMode(next);
            // When entering week view, snap weekStart to the week containing today
            // so the user always lands on a relevant week.
            if (next === 'week') setWeekStart(startOfWeek(new Date()));
            setIsViewFading(false);
        }, 150);
    };

    // Touch-swipe navigation on mobile. Threshold prevents accidental swipes
    // from tap jitter. Mirrors the drag pattern used elsewhere (see Menu.js).
    const swipeStartXRef = useRef(null);
    const handleSwipeStart = (e) => {
        swipeStartXRef.current = e.touches[0].clientX;
    };
    const handleSwipeEnd = (e) => {
        const startX = swipeStartXRef.current;
        swipeStartXRef.current = null;
        if (startX === null) return;
        const delta = e.changedTouches[0].clientX - startX;
        if (Math.abs(delta) < 60) return;
        if (delta > 0) goToPrevMonth();
        else goToNextMonth();
    };

    // After the first fetch, snap the displayed month to the month of the
    // most-recent session so the user lands on something populated.
    const hasSnappedToLatestRef = useRef(false);
    useEffect(() => {
        if (hasSnappedToLatestRef.current) return;
        if (sessionHistory.length === 0) return;
        const latest = sessionHistory.reduce((acc, s) => {
            const d = new Date(s.startedAt);
            return !acc || d > acc ? d : acc;
        }, null);
        if (latest) {
            setCurrentMonth(new Date(latest.getFullYear(), latest.getMonth(), 1));
            hasSnappedToLatestRef.current = true;
        }
    }, [sessionHistory]);

    // Time format helpers
    const to24Hour = (time12) => {
        if (!time12) return '';
        const match = time12.match(/^(\d{1,2}):(\d{2})\s*(am|pm)$/i);
        if (!match) return time12; // already 24h or unrecognized
        let [, h, m, period] = match;
        h = parseInt(h, 10);
        if (period.toLowerCase() === 'pm' && h !== 12) h += 12;
        if (period.toLowerCase() === 'am' && h === 12) h = 0;
        return `${h.toString().padStart(2, '0')}:${m}`;
    };

    const to12Hour = (time24) => {
        if (!time24) return '';
        const match = time24.match(/^(\d{1,2}):(\d{2})$/);
        if (!match) return time24; // already 12h or unrecognized
        let [, h, m] = match;
        h = parseInt(h, 10);
        const period = h >= 12 ? 'pm' : 'am';
        if (h === 0) h = 12;
        else if (h > 12) h -= 12;
        return `${h}:${m} ${period}`;
    };

    const fetchSessions = async () => {
        try {
            const response = await authFetch(`${API_BASE_URL}/api/sessions`);
            if (!response.ok) {
                throw new Error('Failed to fetch sessions');
            }
            const data = await response.json();
            setSessionHistory(data);
            onSessionsUpdate?.(data);
        } catch (error) {
            console.error('Error fetching sessions:', error);
            toast.error('Failed to load sessions');
        }
    };

    useEffect(() => {
        fetchSessions();
    }, []);

    // Fetch projects for dropdown
    useEffect(() => {
        const fetchProjects = async () => {
            try {
                const response = await authFetch(`${API_BASE_URL}/api/projects`);
                if (response.ok) {
                    const data = await response.json();
                    setProjects(data.projects || []);
                }
            } catch (error) {
                console.error('Error fetching projects:', error);
            }
        };
        fetchProjects();
    }, []);

    // Update editingFields when editingSession changes. The wire-level canonical
    // timestamp is `startedAt`; split it into the dialog's two form fields (date,
    // 24h time) for the existing two-input UI.
    useEffect(() => {
        if (editingSession) {
            const d = new Date(editingSession.startedAt);
            const year = d.getFullYear();
            const month = (d.getMonth() + 1).toString().padStart(2, '0');
            const day = d.getDate().toString().padStart(2, '0');
            const hh = d.getHours().toString().padStart(2, '0');
            const mm = d.getMinutes().toString().padStart(2, '0');
            setEditingFields({
                text: editingSession.text || '',
                date: `${year}-${month}-${day}`,
                time: `${hh}:${mm}`,
                duration: editingSession.duration || ''
            });
            setEditProjectId(editingSession.project?._id || '');
        }
    }, [editingSession]);

    // Expose fetchSessions to parent component
    useImperativeHandle(ref, () => ({
        fetchSessions
    }));

    const formatDuration = (duration) => {
        if (!duration) return '0:00';
        const parts = duration.split(':').map(Number);
        let hours, minutes, seconds;
        if (parts.length === 3) {
            // Already HH:MM:SS format
            [hours, minutes, seconds] = parts;
        } else if (parts.length === 2) {
            // Legacy MM:SS — minutes may be > 59
            hours = Math.floor(parts[0] / 60);
            minutes = parts[0] % 60;
            seconds = parts[1];
        } else {
            return duration;
        }
        if (hours > 0) {
            return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
        }
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    const handleClearHistoryClick = () => {
        setIsClearHistoryDialogOpen(true);
    };

    const clearHistory = async () => {
        try {
            const response = await authFetch(`${API_BASE_URL}/api/sessions/clear`, {
                method: 'DELETE',
            });
            
            if (!response.ok) {
                throw new Error('Failed to clear sessions');
            }
            
            setSessionHistory([]);
            toast.success('Session history cleared');
        } catch (error) {
            console.error('Error clearing sessions:', error);
            toast.error('Failed to clear sessions');
        }
    };

    const handleEdit = (session) => {
        setEditingSession(session);
        setIsEditDialogOpen(true);
    };

    const handleDeleteClick = (session) => {
        setSessionToDelete(session);
        setIsDeleteDialogOpen(true);
    };

    const handleDelete = async () => {
        if (!sessionToDelete) return;

        try {
            const response = await authFetch(`${API_BASE_URL}/api/sessions/${sessionToDelete._id}`, {
                method: 'DELETE',
            });

            if (!response.ok) {
                throw new Error('Failed to delete session');
            }

            setSessionHistory(prev => prev.filter(session => session._id !== sessionToDelete._id));
            toast.success('Session deleted successfully');
        } catch (error) {
            console.error('Error deleting session:', error);
            toast.error('Failed to delete session');
        } finally {
            setSessionToDelete(null);
        }
    };

    // Add duration validation helper
    const validateDuration = (duration) => {
        if (!duration || duration === '00:00:00' || duration === '00:00') {
            return 'Please enter a valid duration';
        }

        const parts = duration.split(':').map(Number);
        if (parts.length === 3) {
            const [hours, minutes, seconds] = parts;
            if (isNaN(hours) || isNaN(minutes) || isNaN(seconds)) {
                return 'Invalid duration format';
            }
            if (hours === 0 && minutes === 0 && seconds === 0) {
                return 'Duration cannot be zero';
            }
        } else if (parts.length === 2) {
            const [minutes, seconds] = parts;
            if (isNaN(minutes) || isNaN(seconds)) {
                return 'Invalid duration format';
            }
            if (minutes === 0 && seconds === 0) {
                return 'Duration cannot be zero';
            }
        } else {
            return 'Invalid duration format';
        }

        return '';
    };

    // Add duration formatting helper
    const formatDurationInput = (input, showError) => {
        // Remove non-numeric characters
        const numbers = input.replace(/[^\d]/g, '');
        
        if (numbers.length <= 2) {
            // Just seconds
            return numbers;
        } else if (numbers.length <= 4) {
            // MM:SS
            return `${numbers.slice(0, -2)}:${numbers.slice(-2)}`;
        } else {
            // HH:MM:SS
            return `${numbers.slice(0, -4)}:${numbers.slice(-4, -2)}:${numbers.slice(-2)}`;
        }
    };

    const handleDurationChange = (value) => {
        const showError = () => {
            toast.error('Please enter a valid time');
            setDurationError('Please enter a valid time');
        };
        const formatted = formatDurationInput(value, showError);
        setDurationValue(formatted);
        setDurationError(validateDuration(formatted));
        return formatted;
    };

    const handleSaveEdit = async () => {
        if (!editingSession) return;

        // Validate duration before saving
        const durationValidationError = validateDuration(editingFields.duration);
        if (durationValidationError) {
            toast.error(durationValidationError);
            return;
        }

        try {
            // Combine the dialog's two form fields (date + 24h time) into a
            // single canonical `startedAt` epoch ms in local timezone.
            const [year, month, day] = editingFields.date.split('-').map(Number);
            const [hh, mm] = (editingFields.time || '').split(':').map(Number);
            const startedAt = new Date(year, month - 1, day, hh || 0, mm || 0, 0, 0).getTime();

            const payload = {
                text: editingFields.text.trim(),
                startedAt,
                duration: editingFields.duration,
                project: editProjectId || null
            };

            const response = await authFetch(`${API_BASE_URL}/api/sessions/${editingSession._id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                throw new Error('Failed to update session');
            }

            // Use the server response (populated with project name) instead of local object
            const savedSession = await response.json();
            const updatedSessions = sessionHistory.map(session =>
                session._id === editingSession._id ? savedSession : session
            );
            setSessionHistory(updatedSessions);
            toast.success('Session updated successfully');
            setIsEditDialogOpen(false);
            setEditingSession(null);
            setEditingFields({
                text: '',
                date: '',
                time: '',
                duration: ''
            });
            setEditProjectId('');
        } catch (error) {
            console.error('Error updating session:', error);
            toast.error('Failed to update session');
        }
    };

    const handleExport = async () => {
        try {
            const response = await authFetch(`${API_BASE_URL}/api/sessions`);
            if (!response.ok) {
                throw new Error('Failed to fetch sessions');
            }
            const sessions = await response.json();
            
            const blob = new Blob([JSON.stringify(sessions, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            
            const link = document.createElement('a');
            link.href = url;
            link.download = `sessions_backup_${new Date().toISOString().split('T')[0]}.json`;
            document.body.appendChild(link);
            link.click();
            
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            
            toast.success('Sessions exported successfully');
        } catch (error) {
            console.error('Error exporting sessions:', error);
            toast.error('Failed to export sessions');
        }
    };

    const handleImportClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileSelect = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        if (file.type !== 'application/json') {
            toast.error('Please select a JSON file');
            return;
        }

        try {
            const reader = new FileReader();
            reader.onload = async (e) => {
                try {
                    const result = e.target?.result;
                    if (typeof result !== 'string') return;
                    
                    const sessions = JSON.parse(result);
                    
                    const response = await authFetch(`${API_BASE_URL}/api/sessions/restore`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify(sessions),
                    });

                    if (!response.ok) {
                        throw new Error('Failed to restore sessions');
                    }

                    const data = await response.json();
                    toast.success(`Successfully restored ${data.count} sessions`);
                    
                    if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                    }

                    fetchSessions();
                } catch (error) {
                    console.error('Error parsing or restoring sessions:', error);
                    toast.error('Failed to restore sessions. Please check the file format.');
                }
            };
            reader.readAsText(file);
        } catch (error) {
            console.error('Error reading file:', error);
            toast.error('Failed to read the file');
        }
    };

    const handleCreateSession = async (formData) => {
        const durationValidationError = validateDuration(formData.duration);
        if (durationValidationError) {
            toast.error(durationValidationError);
            return;
        }

        try {
            // Combine the dialog's two form fields (date + 24h time) into a
            // single canonical `startedAt` epoch ms in local timezone.
            const [year, month, day] = formData.date.split('-').map(Number);
            const [hh, mm] = (formData.time || '').split(':').map(Number);
            const startedAt = new Date(year, month - 1, day, hh || 0, mm || 0, 0, 0).getTime();

            const response = await authFetch(`${API_BASE_URL}/api/sessions`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    text: formData.notes?.trim() || '',
                    startedAt,
                    duration: formData.duration,
                    project: formData.project || null
                }),
            });

            if (!response.ok) {
                throw new Error('Failed to create session');
            }

            fetchSessions();
            setShowCreateDialog(false);
            setDurationValue('00:00:00');
            setDurationError('');
            setCreateProjectId('');
            setCreateDate(new Date().toISOString().split('T')[0]);
            setCreateTime(new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' }));
            toast.success('Session created successfully');
        } catch (error) {
            console.error('Error creating session:', error);
            toast.error('Failed to create session');
        }
    };

    return (
        <div className="tw-fixed tw-inset-0 tw-bg-black/90 tw-backdrop-blur-lg tw-z-50">
            <div className="tw-h-full tw-overflow-y-auto tw-text-white">
                {/* Sticky page header — back arrow + title + view toggle */}
                <div className="tw-sticky tw-top-0 tw-z-20 tw-bg-black/90 tw-backdrop-blur-lg tw-px-2 sm:tw-px-6 tw-py-3 tw-flex tw-items-center tw-justify-between">
                    <div className="tw-flex tw-items-center">
                        <button
                            onClick={onClose}
                            className="tw-appearance-none tw-bg-transparent tw-border-none tw-p-0 tw-m-0 tw-mr-4 tw-text-white/70 hover:tw-text-white tw-cursor-pointer tw-transition-colors"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="tw-w-6 tw-h-6">
                                <path d="M19 12H5M12 19l-7-7 7-7"/>
                            </svg>
                        </button>
                        <h2 className="tw-text-xl tw-font-bold tw-text-white">Session History</h2>
                    </div>
                    {/* Week / Month toggle */}
                    <div className="tw-inline-flex tw-rounded-full tw-bg-white/10 tw-p-1 tw-text-xs sm:tw-text-sm">
                        <button
                            type="button"
                            onClick={() => switchView('week')}
                            className={`tw-appearance-none tw-border-0 tw-cursor-pointer tw-px-3 tw-py-1 tw-rounded-full tw-transition-colors ${viewMode === 'week' ? 'tw-bg-white/20 tw-text-white tw-font-semibold' : 'tw-bg-transparent tw-text-white/70 hover:tw-text-white'}`}
                        >
                            Week
                        </button>
                        <button
                            type="button"
                            onClick={() => switchView('month')}
                            className={`tw-appearance-none tw-border-0 tw-cursor-pointer tw-px-3 tw-py-1 tw-rounded-full tw-transition-colors ${viewMode === 'month' ? 'tw-bg-white/20 tw-text-white tw-font-semibold' : 'tw-bg-transparent tw-text-white/70 hover:tw-text-white'}`}
                        >
                            Month
                        </button>
                    </div>
                </div>
                <div className="tw-p-2 sm:tw-p-6">

                    {/* Day-details modal — mounted before Edit/Delete so those
                        stack on top of it when both are open (equal z-index, later wins). */}
                    <SessionDetailsModal
                        darkMode
                        isOpen={selectedDay !== null}
                        onClose={() => setSelectedDay(null)}
                        dateLabel={selectedDay ? selectedDay.toLocaleDateString(undefined, {
                            weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
                        }) : ''}
                        sessions={selectedDay
                            ? (sessionsByDay[dayKey(selectedDay.getFullYear(), selectedDay.getMonth(), selectedDay.getDate())] || [])
                            : []}
                        onEditSession={handleEdit}
                        onDeleteSession={handleDeleteClick}
                        formatDuration={formatDuration}
                    />

                    {/* Edit Dialog */}
                    <EditDialog
                        darkMode
                        isOpen={isEditDialogOpen}
                        onClose={() => {
                            setIsEditDialogOpen(false);
                            setEditingSession(null);
                            setEditingFields({
                                text: '',
                                date: '',
                                time: '',
                                duration: ''
                            });
                            setEditProjectId('');
                        }}
                        onConfirm={handleSaveEdit}
                        title="Edit Session"
                        fields={[
                            {
                                id: 'date',
                                label: 'Date',
                                type: 'date',
                                value: editingFields.date,
                                onChange: (value) => setEditingFields(prev => ({ ...prev, date: value })),
                                required: true
                            },
                            {
                                id: 'time',
                                label: 'Time',
                                type: 'time',
                                value: editingFields.time,
                                onChange: (value) => setEditingFields(prev => ({ ...prev, time: value })),
                                required: true
                            },
                            {
                                id: 'duration',
                                label: 'Duration',
                                type: 'custom',
                                value: editingFields.duration,
                                onChange: (value) => {
                                    setEditingFields(prev => ({ ...prev, duration: value }));
                                    setDurationError(validateDuration(value));
                                },
                                renderInput: ({ value, onChange, error }) => (
                                    <DurationInput
                                        value={value}
                                        onChange={onChange}
                                        error={error}
                                    />
                                ),
                                required: true,
                                error: durationError
                            },
                            {
                                id: 'project',
                                label: 'Project',
                                type: 'select',
                                value: editProjectId,
                                onChange: (value) => setEditProjectId(value),
                                options: [
                                    { value: '', label: 'No Project' },
                                    ...projects.map(p => ({ value: p._id, label: p.name }))
                                ]
                            },
                            {
                                id: 'notes',
                                label: 'Session Notes',
                                type: 'textarea',
                                value: editingFields.text,
                                onChange: (value) => setEditingFields(prev => ({ ...prev, text: value })),
                                placeholder: 'Enter session notes...'
                            }
                        ]}
                    />

                    {/* Delete Dialog */}
                    <DeleteDialog
                        darkMode
                        isOpen={isDeleteDialogOpen}
                        onClose={() => {
                            setIsDeleteDialogOpen(false);
                            setSessionToDelete(null);
                        }}
                        onConfirm={handleDelete}
                        title="Delete Session"
                        message={sessionToDelete ? `Are you sure you want to delete this session from ${new Date(sessionToDelete.startedAt).toLocaleDateString()}?` : ''}
                    />

                    {/* Clear History Dialog */}
                    <DeleteDialog
                        darkMode
                        isOpen={isClearHistoryDialogOpen}
                        onClose={() => setIsClearHistoryDialogOpen(false)}
                        onConfirm={clearHistory}
                        title="Clear All Session History"
                        message="Are you sure you want to clear all session history? This action cannot be undone."
                    />

                    {/* Calendar View */}
                    <div
                        className="tw-mb-20"
                        onTouchStart={handleSwipeStart}
                        onTouchEnd={handleSwipeEnd}
                    >
                        <div className="tw-flex tw-items-center tw-justify-center tw-gap-4 tw-mb-3">
                            <button
                                type="button"
                                onClick={handlePrev}
                                disabled={!canPrev}
                                aria-label={viewMode === 'week' ? 'Previous week' : 'Previous month'}
                                className="tw-appearance-none tw-bg-transparent tw-border-0 tw-cursor-pointer tw-text-white/70 hover:tw-text-white disabled:tw-opacity-30 disabled:tw-cursor-not-allowed tw-p-1"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="15 18 9 12 15 6" />
                                </svg>
                            </button>
                            <div className="tw-text-lg tw-font-semibold tw-text-white tw-min-w-[160px] tw-text-center">
                                {viewMode === 'week'
                                    ? formatWeekHeader(weekStart)
                                    : currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}
                            </div>
                            <button
                                type="button"
                                onClick={handleNext}
                                disabled={!canNext}
                                aria-label={viewMode === 'week' ? 'Next week' : 'Next month'}
                                className="tw-appearance-none tw-bg-transparent tw-border-0 tw-cursor-pointer tw-text-white/70 hover:tw-text-white disabled:tw-opacity-30 disabled:tw-cursor-not-allowed tw-p-1"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="9 18 15 12 9 6" />
                                </svg>
                            </button>
                        </div>
                        <div
                            className={`tw-transition-opacity tw-duration-150 ${isViewFading ? 'tw-opacity-0' : 'tw-opacity-100'}`}
                        >
                            <MonthCalendar
                                year={currentMonth.getFullYear()}
                                month={currentMonth.getMonth()}
                                weekStart={viewMode === 'week' ? weekStart : null}
                                today={today}
                                darkMode
                                onDayClick={(date) => {
                                    const key = dayKey(date.getFullYear(), date.getMonth(), date.getDate());
                                    if (!sessionsByDay[key] || sessionsByDay[key].length === 0) return;
                                    setSelectedDay(date);
                                }}
                                renderDayContent={(date) => {
                                    const key = dayKey(date.getFullYear(), date.getMonth(), date.getDate());
                                    const sessions = sessionsByDay[key];
                                    if (!sessions || sessions.length === 0) return null;
                                    const visible = sessions.slice(0, 3);
                                    const overflow = sessions.length > 3;
                                    return (
                                        <div className="tw-flex tw-items-center tw-justify-center tw-gap-0.5 tw-mt-auto tw-pb-1">
                                            {visible.map((s) => (
                                                <span
                                                    key={s._id}
                                                    className="tw-w-2 tw-h-2 tw-rounded-full"
                                                    style={{ backgroundColor: colorForSession(s._id) }}
                                                />
                                            ))}
                                            {overflow && (
                                                <span className="tw-text-xs tw-text-white/60 tw-leading-none tw-font-semibold">+</span>
                                            )}
                                        </div>
                                    );
                                }}
                            />
                        </div>
                        {sessionHistory.length === 0 && (
                            <p className="tw-text-center tw-text-white/60 tw-mt-6">
                                No sessions yet. Add one to get started.
                            </p>
                        )}
                    </div>

                    {/* Footer Actions - Now fixed at bottom */}
                    <div className="tw-fixed tw-bottom-0 tw-left-0 tw-right-0 tw-bg-black/80 tw-backdrop-blur-lg tw-p-2 sm:tw-p-4 tw-border-t tw-border-white/10">
                        <div className="tw-flex tw-justify-between tw-items-center">
                            <div className="tw-flex tw-items-center tw-gap-2 sm:tw-gap-4">
                                <button
                                    onClick={handleExport}
                                    className="tw-p-2 tw-rounded-lg tw-bg-white/10 hover:tw-bg-white/20 tw-transition-colors"
                                    title="Export Sessions"
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="tw-h-5 tw-w-5 tw-text-white/80"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                        <polyline points="7 10 12 15 17 10" />
                                        <line x1="12" y1="15" x2="12" y2="3" />
                                    </svg>
                                </button>
                                <button
                                    onClick={handleImportClick}
                                    className="tw-p-2 tw-rounded-lg tw-bg-white/10 hover:tw-bg-white/20 tw-transition-colors"
                                    title="Import Sessions"
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="tw-h-5 tw-w-5 tw-text-white/80"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                        <polyline points="17 8 12 3 7 8" />
                                        <line x1="12" y1="3" x2="12" y2="15" />
                                    </svg>
                                </button>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".json,application/json"
                                    onChange={handleFileSelect}
                                    className="tw-hidden"
                                />
                            </div>
                            <div className="tw-flex tw-items-center tw-gap-2 sm:tw-gap-4">
                                <button
                                    onClick={() => setShowCreateDialog(true)}
                                    className="primary-button tw-h-10 tw-w-auto tw-px-3 sm:tw-w-36 sm:tw-px-0 tw-flex tw-items-center tw-justify-center tw-text-sm"
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="tw-h-4 tw-w-4 tw-mr-2"
                                        viewBox="0 0 20 15"
                                        fill="currentColor"
                                    >
                                        <path fillRule="evenodd" d="M10 3a1 1 0 00-1 1v5H4a1 1 0 100 2h5v5a1 1 0 102 0v-5h5a1 1 0 100-2h-5V4a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                    Add Session
                                </button>
                                <button
                                    onClick={handleClearHistoryClick}
                                    className="danger-button tw-h-10 tw-w-auto tw-px-3 sm:tw-w-36 sm:tw-px-0 tw-flex tw-items-center tw-justify-center tw-text-sm"
                                >
                                    Clear History
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <CreateDialog
                darkMode
                isOpen={showCreateDialog}
                onClose={() => {
                    setShowCreateDialog(false);
                    setDurationValue('00:00:00');
                    setDurationError('');
                    setCreateProjectId('');
                    setCreateDate(new Date().toISOString().split('T')[0]);
                    setCreateTime(new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' }));
                }}
                onSubmit={handleCreateSession}
                title="Add New Session"
                fields={[
                    {
                        name: 'date',
                        label: 'Date',
                        type: 'date',
                        required: true,
                        value: createDate,
                        onChange: (value) => setCreateDate(value)
                    },
                    {
                        name: 'time',
                        label: 'Time',
                        type: 'time',
                        required: true,
                        value: createTime,
                        onChange: (value) => setCreateTime(value)
                    },
                    {
                        name: 'duration',
                        label: 'Duration',
                        type: 'custom',
                        value: durationValue,
                        onChange: (value) => {
                            setDurationValue(value);
                            setDurationError(validateDuration(value));
                        },
                        renderInput: ({ value, onChange, error }) => (
                            <DurationInput
                                value={value}
                                onChange={onChange}
                                error={error}
                            />
                        ),
                        required: true,
                        error: durationError
                    },
                    {
                        name: 'project',
                        label: 'Project',
                        type: 'select',
                        value: createProjectId,
                        onChange: (value) => setCreateProjectId(value),
                        options: [
                            { value: '', label: 'No Project' },
                            ...projects.map(p => ({ value: p._id, label: p.name }))
                        ]
                    },
                    {
                        name: 'notes',
                        label: 'Session Notes',
                        type: 'textarea',
                        placeholder: 'Enter session notes...',
                        required: false
                    }
                ]}
                submitButtonText="Create"
            />
        </div>
    );
});

SessionHistory.defaultProps = {
    onSessionsUpdate: () => {} // Default no-op function
};

export default SessionHistory;